// Cross-border verdict math
// Tells the user whether crossing the border is worth it for a specific product.
// All values in CHF. Conservative estimates - we round UP costs and DOWN savings.

// Distances (one-way km from Swiss city to nearest border crossing in target country)
// Verified May 2026 via Google Maps. Update if Swiss roads change significantly.
export const DISTANCES = {
  Basel:       { de: 5,   fr: 7   },  // Weil am Rhein / Saint-Louis
  Geneva:      { de: 380, fr: 4   },  // Saint-Genis-Pouilly very close; DE via Zurich
  Zurich:      { de: 65,  fr: 220 },  // Konstanz / Mulhouse
  Lausanne:    { de: 290, fr: 50  },  // Évian-les-Bains
  Bern:        { de: 145, fr: 95  },  // Pontarlier (FR) / Lörrach (DE)
  Lucerne:     { de: 100, fr: 165 },  // Konstanz / Saint-Louis
  StGallen:    { de: 35,  fr: 280 },  // Konstanz / Bregenz
  Schaffhausen:{ de: 5,   fr: 200 },  // Already on the German border
  Lugano:      { de: 250, fr: 380 },  // Realistically users go to IT, but if forced
  Sion:        { de: 220, fr: 110 },  // Évian
  Other:       { de: 100, fr: 100 },  // Generic fallback - flag clearly in UI
};

// Conservative fuel prices (CHF/litre) - update quarterly
export const FUEL_PRICES = {
  petrol: 1.85,
  diesel: 1.95,
  electric: 0,
};

// Default vehicle consumption (L/100km) by type
export const DEFAULT_CONSUMPTION = {
  petrol: 7.5,
  diesel: 6.0,
  electric: 0,
};

const SWISS_VAT = 0.081; // 8.1%
const ALLOWANCE_CHF = 150;
const WORTH_IT_THRESHOLD = 50;
const BORDERLINE_THRESHOLD = 0;

/**
 * Calculate the verdict for a single foreign price vs Swiss price.
 *
 * @param {object} params
 * @param {number} params.swissPriceChf - Cheapest Swiss price (CHF). Required.
 * @param {number} params.foreignPrice - Foreign price in foreign currency.
 * @param {string} params.foreignCountry - 'fr' or 'de'.
 * @param {number} params.exchangeRate - 1 CHF = X EUR (we receive CHF→EUR).
 * @param {object} params.profile - Travel profile from localStorage.
 * @returns {object} verdict info or null if cannot compute
 */
export function calculateVerdict({ swissPriceChf, foreignPrice, foreignCountry, exchangeRate, profile }) {
  if (!swissPriceChf || !foreignPrice || !foreignCountry) return null;
  if (!profile || !profile.city || !profile.vehicleType) return null;

  // 1. Convert foreign price to CHF (we have CHF→EUR rate, so divide)
  // Both FR and DE use EUR
  const foreignPriceChf = foreignPrice / (exchangeRate || 0.95);

  // 2. Add Swiss VAT if over allowance (on TOTAL value, not just excess)
  const swissVatChf = foreignPriceChf > ALLOWANCE_CHF ? foreignPriceChf * SWISS_VAT : 0;

  // 3. Fuel cost (round trip)
  const distances = DISTANCES[profile.city] || DISTANCES.Other;
  const oneWayKm = distances[foreignCountry];
  const roundTripKm = oneWayKm * 2;
  const consumption = profile.consumption || DEFAULT_CONSUMPTION[profile.vehicleType] || 7.5;
  const fuelPricePerLitre = FUEL_PRICES[profile.vehicleType] || FUEL_PRICES.petrol;
  const litresUsed = (roundTripKm * consumption) / 100;
  const fuelCostChf = litresUsed * fuelPricePerLitre;

  // 4. Total cost of crossing
  const crossingTotalChf = foreignPriceChf + swissVatChf + fuelCostChf;

  // 5. Savings vs staying in Switzerland
  const savingsChf = swissPriceChf - crossingTotalChf;

  // 6. Verdict
  let verdict;
  if (savingsChf >= WORTH_IT_THRESHOLD) {
    verdict = 'worth_it';
  } else if (savingsChf > BORDERLINE_THRESHOLD) {
    verdict = 'borderline';
  } else {
    verdict = 'not_worth';
  }

  return {
    verdict,
    swissPriceChf: Math.round(swissPriceChf),
    foreignPriceChf: Math.round(foreignPriceChf),
    foreignPrice: Math.round(foreignPrice),
    swissVatChf: Math.round(swissVatChf),
    fuelCostChf: Math.round(fuelCostChf),
    crossingTotalChf: Math.round(crossingTotalChf),
    savingsChf: Math.round(savingsChf),
    roundTripKm,
    foreignCountry,
    overAllowance: foreignPriceChf > ALLOWANCE_CHF,
    canTaxFreeRefund: (foreignCountry === 'de' && foreignPrice > 50.01)
                   || (foreignCountry === 'fr' && foreignPrice > 100.01),
  };
}
